import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppSelector } from '@/hooks';
import { permisosService } from '@/api/permisosService';

export interface VerificarResult {
  permitido: boolean;
  requiereConfirmacion: boolean;
  funcionalidad?: string;
}

export function usePermiso() {
  const user      = useAppSelector(s => s.auth.user);
  const empresaId = user?.empresaId ?? 0;
  const roles     = user?.roles ?? [];
  const esSuperAdmin = roles.includes('SuperAdmin');
  // El rol puede venir del token o del perfil POS en la empresa activa.
  const rolBase      = user?.rolBase;
  const esAdmin      = roles.includes('Admin') || rolBase === 'Admin';
  const esSupervisor = roles.includes('Supervisor') || rolBase === 'Supervisor';

  // Verifica si el usuario actual tiene un permiso (sin confirmación)
  const verificar = useCallback(async (codigo: string): Promise<VerificarResult> => {
    if (!empresaId) return { permitido: false, requiereConfirmacion: false };
    if (esSuperAdmin) return { permitido: true, requiereConfirmacion: false };
    try {
      return await permisosService.verificarPermiso(empresaId, codigo);
    } catch {
      return { permitido: false, requiereConfirmacion: false };
    }
  }, [empresaId, esSuperAdmin]);

  // Verifica si el usuario puede AUTORIZAR acciones restringidas
  const puedeAutorizar = esSuperAdmin || esAdmin || esSupervisor;

  return { verificar, puedeAutorizar, empresaId, roles, rolBase, esSuperAdmin, esAdmin, esSupervisor };
}

/**
 * Funcionalidades que el usuario puede usar en la empresa activa, según el
 * rol de su perfil. `puede(codigo)` sirve para ocultar botones; el backend
 * vuelve a validar cada acción.
 */
export function useMisPermisos() {
  const empresaId = useAppSelector(s => s.auth.user?.empresaId ?? 0);
  const q = useQuery({
    queryKey: ['mis-permisos', empresaId],
    queryFn: () => permisosService.misPermisos(empresaId),
    enabled: empresaId > 0,
    staleTime: 5 * 60_000,
  });
  const set = new Set(q.data?.funcionalidades ?? []);
  const esAdmin = (q.data?.roles ?? []).some(r => r === 'Admin' || r === 'SuperAdmin');
  const puede = (codigo: string) => esAdmin || set.has(codigo);
  return { ...q, puede, puedeAutorizar: q.data?.puedeAutorizar ?? false };
}
